import { ErrorBanner, SkeletonText } from '@/components';
import { FormSection } from '@/components/FormSection';
import { useTranslation } from 'react-i18next';
import type { FieldSalesRepresentativeCompanyAndContact } from '../api';
import { useFieldSalesRepresentativesAsCustomer } from '../hooks';
import { FieldSalesRepresentativeCompanyContactList } from './FieldSalesRepresentativeCompanyContactList';

interface FieldSalesRepresentativeReadOnlySectionProps {
  companyId: string;
}

/**
 * @component FieldSalesRepresentativeReadOnlySection
 * @description Loads and displays the representatives assigned to the current company as customer.
 *
 * @props
 *   companyId (string, required) - Customer company ID used for the as-customer endpoint.
 *
 * @changelog
 *   2026-07-08 - Initial creation.
 */
export function FieldSalesRepresentativeReadOnlySection({
  companyId,
}: FieldSalesRepresentativeReadOnlySectionProps) {
  const { t } = useTranslation();
  const {
    data: representatives = [],
    isLoading,
    isError,
  } = useFieldSalesRepresentativesAsCustomer(companyId);

  return (
    <FormSection
      icon="contact"
      title={t('companyAdmin.form.fieldSalesRepresentative.title')}
    >
      <ErrorBanner hasError={isError}>
        {t('companyAdmin.form.fieldSalesRepresentative.loadError')}
      </ErrorBanner>
      <ReadOnlySectionContent
        isLoading={isLoading}
        representatives={representatives}
      />
    </FormSection>
  );
}

function ReadOnlySectionContent({
  isLoading,
  representatives,
}: ReadOnlySectionContentProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      // Lightweight loading placeholder for the representative list.
      <div className="border-border bg-surface rounded-lg border p-3">
        <SkeletonText lines={3} />
      </div>
    );
  }

  if (representatives.length === 0) {
    return (
      // Empty state when no representative is assigned to this customer.
      <p className="text-muted text-sm">
        {t('companyAdmin.form.fieldSalesRepresentative.noRepresentatives')}
      </p>
    );
  }

  return (
    <FieldSalesRepresentativeCompanyContactList
      representatives={representatives}
    />
  );
}

interface ReadOnlySectionContentProps {
  isLoading: boolean;
  representatives: FieldSalesRepresentativeCompanyAndContact[];
}
